import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { finalize } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { LanguageService } from '../../services/language.service';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  templateUrl: './forgot-password.html',
  styleUrls: ['./forgot-password.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    FloatLabelModule,
    InputTextModule,
    TranslateModule
  ]
})
export class ForgotPasswordComponent implements OnInit {
  readonly languageService = inject(LanguageService);

  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly form = this.fb.group({
    email: ['', [Validators.required, Validators.email]]
  });

  loading = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  ngOnInit(): void {
    const email = this.route.snapshot.queryParamMap.get('email')?.trim();
    const currentUserEmail = this.authService.getCurrentUser()?.email?.trim();
    const initialEmail = email || currentUserEmail || '';

    if (initialEmail) {
      this.form.patchValue({ email: initialEmail });
    }
  }

  sendResetEmail(): void {
    this.errorMessage = null;
    this.successMessage = null;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const email = this.form.get('email')?.value?.trim();
    if (!email) {
      this.errorMessage = 'FORGOT_PASSWORD.INVALID_EMAIL';
      this.cdr.markForCheck();
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    this.authService.sendPasswordResetEmail(email)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.successMessage = 'FORGOT_PASSWORD.SUCCESS';
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.errorMessage = this.mapError(error);
          this.cdr.markForCheck();
        }
      });
  }

  goToLogin(): void {
    const email = this.form.get('email')?.value?.trim();
    const extras = email ? { queryParams: { email } } : {};
    this.router.navigate(['/login'], extras);
  }

  private mapError(error: unknown): string {
    if (typeof error === 'object' && error !== null && 'code' in error) {
      const firebaseError = error as { code: string };
      switch (firebaseError.code) {
        case 'auth/invalid-email':
          return 'AUTH_ERRORS.INVALID_EMAIL';
        case 'auth/too-many-requests':
          return 'AUTH_ERRORS.TOO_MANY_REQUESTS';
        case 'auth/network-request-failed':
          return 'AUTH_ERRORS.NETWORK_ERROR';
        default:
          return 'AUTH_ERRORS.GENERIC';
      }
    }

    return 'AUTH_ERRORS.GENERIC';
  }
}
